import React, { useEffect, useMemo, useRef, useState } from "react";
import type { User } from "firebase/auth";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { PayMethod, PayStart, Quote, formatDueDate, money, payQuote, saveQuoteDetails } from "../utils/quotes";

const card = "bg-white rounded-3xl p-6 shadow-sm border border-slate-100";
const field = "w-full bg-slate-50 border border-slate-300 rounded-xl py-2 px-3";

const COUNTRY_CODES = ["US", "CA", "GB", "AU", "NZ", "IE", "DE", "FR", "ES", "IT", "NL", "BE", "CH", "AT", "SE", "NO", "DK", "FI", "PT", "MX", "BR", "AR", "CL", "CO", "IN", "SG", "JP", "KR", "AE", "ZA"];
const countryName = (code: string) => {
  try {
    return new Intl.DisplayNames(["en"], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
};

const EMPTY = { name: "", company: "", line1: "", line2: "", city: "", state: "", postalCode: "", country: "US", eventAt: "", notes: "" };
type Form = typeof EMPTY;

const fromQuote = (q: Quote): Form => ({
  name: q.details.name ?? "", company: q.details.company ?? "", line1: q.details.line1 ?? "", line2: q.details.line2 ?? "", city: q.details.city ?? "",
  state: q.details.state ?? "", postalCode: q.details.postalCode ?? "", country: q.details.country || "US", eventAt: q.details.eventAt ?? "", notes: q.details.notes ?? "",
});

/** Takes the payment inside the page with Stripe's Payment Element. */
const PayForm: React.FC<{ total: number; method: PayMethod; onSubmitted: () => void }> = ({ total, method, onSubmitted }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    setError(null);
    const result = await stripe.confirmPayment({ elements, confirmParams: { return_url: `${window.location.origin}/#/billing` }, redirect: "if_required" });
    if (result.error) {
      setError(result.error.message ?? "The payment did not go through. Please check your details and try again.");
      setBusy(false);
    } else {
      onSubmitted();
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <PaymentElement />
      {error && <p className="text-red-700 text-sm" role="alert">{error}</p>}
      <button type="submit" className="btn-primary" disabled={!stripe || busy}>
        {busy ? "Processing..." : `Pay ${money(total)}${method === "ach" ? " by bank transfer" : ""}`}
      </button>
    </form>
  );
};

const QuotePanel: React.FC<{ user: User; quote: Quote; onChanged: () => void }> = ({ user, quote, onChanged }) => {
  const [form, setForm] = useState<Form>(() => fromQuote(quote));
  const [editing, setEditing] = useState(!quote.hasDetails);
  const [method, setMethod] = useState<PayMethod>("card");
  const [session, setSession] = useState<PayStart | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const polls = useRef(0);

  const stripePromise = useMemo(() => (session ? loadStripe(session.publishableKey) : null), [session]);
  const paid = quote.status === "paid";
  const discounted = quote.discountPercent > 0;

  // After a payment is submitted the webhook marks the quote paid a moment later: check a few times.
  useEffect(() => {
    if (!submitted || paid) return;
    polls.current = 0;
    const timer = setInterval(() => {
      polls.current += 1;
      onChanged();
      if (polls.current >= 15) clearInterval(timer);
    }, 3000);
    return () => clearInterval(timer);
  }, [submitted, paid]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (key: keyof Form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setForm({ ...form, [key]: e.target.value });

  const saveDetails = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await saveQuoteDetails(user, quote.id, form);
      setEditing(false);
      setSession(null);
      onChanged();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const continueToPayment = async () => {
    setBusy(true);
    setError(null);
    try {
      setSession(await payQuote(user, quote.id, method));
    } catch (err) {
      setError(/^quotes\/pay \d+$/.test((err as Error).message) ? "Could not start the payment. Please try again in a moment." : (err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={card} aria-label={`Quote ${quote.invoiceNumber ?? ""}`.trim()}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-xl font-bold text-slate-900">{quote.title}</h2>
          {quote.description && <p className="text-slate-600 text-sm mt-1">{quote.description}</p>}
        </div>
        {paid && <span className="text-xs font-bold uppercase text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">Paid</span>}
      </div>

      <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
        {quote.invoiceNumber && (<><dt className="text-slate-600">Invoice number</dt><dd className="text-slate-900">{quote.invoiceNumber}</dd></>)}
        {quote.poNumber && (<><dt className="text-slate-600">P.O./S.O. number</dt><dd className="text-slate-900">{quote.poNumber}</dd></>)}
        <dt className="text-slate-600">{paid ? "Was due" : "Payment due"}</dt><dd className="text-slate-900">{formatDueDate(quote.dueDate)}</dd>
      </dl>

      <div className="mt-4 border-t border-slate-100 pt-3 text-sm space-y-1">
        {discounted && (
          <>
            <div className="flex justify-between text-slate-600"><span>Price</span><span className="line-through">{money(quote.listCents)}</span></div>
            <div className="flex justify-between text-emerald-700"><span>{quote.discountPercent}% special discount</span><span>-{money(quote.listCents - quote.netCents)}</span></div>
          </>
        )}
        <div className="flex justify-between text-lg font-bold text-slate-900"><span>Amount due (USD)</span><span>{money(quote.netCents)}</span></div>
        {!paid && <p className="text-slate-500 text-xs">A small processing fee for the way you pay is added at the next step.</p>}
      </div>

      {paid ? (
        <div className="mt-4 flex flex-wrap gap-3 items-center">
          {quote.paidAt && <p className="text-sm text-slate-600">Paid on {new Date(quote.paidAt).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}. Thank you!</p>}
          {quote.hostedInvoiceUrl && <a className="btn-secondary inline-block" href={quote.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer">View invoice</a>}
          {quote.invoicePdf && <a className="btn-secondary inline-block" href={quote.invoicePdf} target="_blank" rel="noopener noreferrer">Download PDF</a>}
        </div>
      ) : submitted ? (
        <div className="mt-4 bg-green-50 border border-green-300 text-green-900 rounded-2xl p-4" role="status">
          {method === "ach"
            ? "Thank you. Your bank transfer was submitted. Bank payments take a few business days to clear, and this page will show Paid once they do."
            : "Thank you. Confirming your payment..."}
        </div>
      ) : editing ? (
        <form onSubmit={saveDetails} className="mt-5 space-y-4">
          <h3 className="font-bold text-slate-900">Invoice details</h3>
          <p className="text-slate-600 text-sm">We need these for your invoice.</p>
          <div>
            <label htmlFor={`name-${quote.id}`} className="font-bold text-slate-900 block mb-1">Name on the invoice</label>
            <input id={`name-${quote.id}`} required maxLength={120} className={field} value={form.name} onChange={set("name")} />
          </div>
          <div>
            <label htmlFor={`company-${quote.id}`} className="font-bold text-slate-900 block mb-1">Company name <span className="font-normal text-slate-500">(optional)</span></label>
            <input id={`company-${quote.id}`} maxLength={120} className={field} value={form.company} onChange={set("company")} />
          </div>
          <div>
            <label htmlFor={`line1-${quote.id}`} className="font-bold text-slate-900 block mb-1">Address for the invoice</label>
            <input id={`line1-${quote.id}`} required maxLength={200} className={field} value={form.line1} onChange={set("line1")} placeholder="Street address" />
            <input aria-label="Address line 2" maxLength={200} className={`${field} mt-2`} value={form.line2} onChange={set("line2")} placeholder="Apartment, suite, etc. (optional)" />
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label htmlFor={`city-${quote.id}`} className="font-bold text-slate-900 block mb-1">City</label>
              <input id={`city-${quote.id}`} required maxLength={100} className={field} value={form.city} onChange={set("city")} />
            </div>
            <div>
              <label htmlFor={`state-${quote.id}`} className="font-bold text-slate-900 block mb-1">State or region</label>
              <input id={`state-${quote.id}`} maxLength={100} className={field} value={form.state} onChange={set("state")} />
            </div>
            <div>
              <label htmlFor={`zip-${quote.id}`} className="font-bold text-slate-900 block mb-1">Postal code</label>
              <input id={`zip-${quote.id}`} required maxLength={20} className={field} value={form.postalCode} onChange={set("postalCode")} />
            </div>
          </div>
          <div>
            <label htmlFor={`country-${quote.id}`} className="font-bold text-slate-900 block mb-1">Country</label>
            <select id={`country-${quote.id}`} className={field} value={form.country} onChange={set("country")}>
              {COUNTRY_CODES.map((c) => <option key={c} value={c}>{countryName(c)}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor={`event-${quote.id}`} className="font-bold text-slate-900 block mb-1">Date and time of the event</label>
            <input id={`event-${quote.id}`} required maxLength={100} className={field} value={form.eventAt} onChange={set("eventAt")} placeholder="For example: November 20, 2030, 9:00 AM Eastern" />
          </div>
          <div>
            <label htmlFor={`notes-${quote.id}`} className="font-bold text-slate-900 block mb-1">Notes <span className="font-normal text-slate-500">(printed at the bottom of the invoice)</span></label>
            <textarea id={`notes-${quote.id}`} rows={3} maxLength={1500} className={field} value={form.notes} onChange={set("notes")} />
          </div>
          {error && <p className="text-red-700 text-sm" role="alert">{error}</p>}
          <div className="flex gap-3">
            <button type="submit" className="btn-primary" disabled={busy}>{busy ? "Saving..." : "Save and continue"}</button>
            {quote.hasDetails && <button type="button" className="btn-secondary" onClick={() => { setForm(fromQuote(quote)); setEditing(false); setError(null); }}>Cancel</button>}
          </div>
        </form>
      ) : (
        <div className="mt-5 space-y-4">
          <div className="text-sm text-slate-700 bg-slate-50 rounded-2xl p-4">
            <p className="font-bold text-slate-900">{quote.details.name}{quote.details.company ? `, ${quote.details.company}` : ""}</p>
            <p>{[quote.details.line1, quote.details.line2].filter(Boolean).join(", ")}</p>
            <p>{[quote.details.city, quote.details.state, quote.details.postalCode].filter(Boolean).join(", ")}, {quote.details.country}</p>
            <p className="mt-1">Event: {quote.details.eventAt}</p>
            {!session && <button className="text-blue-600 underline mt-2" onClick={() => setEditing(true)}>Edit invoice details</button>}
          </div>

          {session && stripePromise ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-700">
                {money(session.netCents)} + {money(session.feeCents)} processing fee ({session.method === "ach" ? "bank transfer" : "card"}) = <strong>{money(session.totalCents)}</strong>
              </p>
              <Elements stripe={stripePromise} options={{ clientSecret: session.clientSecret }}>
                <PayForm total={session.totalCents} method={session.method} onSubmitted={() => setSubmitted(true)} />
              </Elements>
              <button className="text-blue-600 underline text-sm" onClick={() => setSession(null)}>Choose a different way to pay</button>
            </div>
          ) : (
            <fieldset className="space-y-2">
              <legend className="font-bold text-slate-900 mb-1">How would you like to pay?</legend>
              {(["card", "ach"] as PayMethod[]).map((m) => (
                <label key={m} className={`flex items-start gap-3 rounded-2xl border p-3 cursor-pointer ${method === m ? "border-blue-500 bg-blue-50" : "border-slate-200"}`}>
                  <input type="radio" name={`method-${quote.id}`} className="mt-1" checked={method === m} onChange={() => setMethod(m)} />
                  <span>
                    <span className="font-bold text-slate-900 block">{m === "card" ? "Credit or debit card" : "US bank transfer"}</span>
                    <span className="text-sm text-slate-600">
                      {money(quote.options[m].feeCents)} processing fee, total {money(quote.options[m].totalCents)}
                      {m === "ach" ? ". Takes a few business days to clear." : ""}
                    </span>
                  </span>
                </label>
              ))}
              {error && <p className="text-red-700 text-sm" role="alert">{error}</p>}
              <button className="btn-primary mt-2" onClick={continueToPayment} disabled={busy}>{busy ? "Loading..." : "Continue to payment"}</button>
            </fieldset>
          )}
        </div>
      )}
    </section>
  );
};

export default QuotePanel;
